// ============================================================================
// Record Creation Metadata & Derivation
// ============================================================================
//
// In accordance with Step 1 of ACTIVITY-CREATION-DATE-BRIEF-v1:
// - Displays the record's creation date as a fixed header fact, distinct from
//   event feed rows.
// - Sourced directly from the record row (recordCreatedAt).
// - Where a record genuinely has no created_at, derives the date from the
//   earliest activity event (or fallbackDate) and labels it "First seen <date>
//   (derived from earliest activity)".
// - No actor name is claimed unless created_by is genuinely stored on the row,
//   and never claimed on a derived date.
// - Never renders a blank or omits the line.
// ============================================================================

export type RecordCreationFact = {
  factLabel: 'Record created' | 'First seen';
  effectiveDate: string | null;
  isDerived: boolean;
  createdBy: string | null;
  createdByName: string | null;
};

export type DeriveRecordCreationParams = {
  recordCreatedAt?: string | null;
  recordCreatedBy?: string | null;
  recordCreatedByName?: string | null;
  fallbackDate?: string | null;
  events?: ReadonlyArray<{created_at?: string | null} | null | undefined>;
};

export function deriveRecordCreationMeta({
  recordCreatedAt,
  recordCreatedBy,
  recordCreatedByName,
  fallbackDate,
  events,
}: DeriveRecordCreationParams): RecordCreationFact {
  const hasDirectCreated = typeof recordCreatedAt === 'string' && recordCreatedAt.trim().length > 0;

  if (hasDirectCreated) {
    return {
      factLabel: 'Record created',
      effectiveDate: recordCreatedAt.trim(),
      isDerived: false,
      createdBy: recordCreatedBy || null,
      createdByName: recordCreatedByName || null,
    };
  }

  // Fallback to record's own explicit date if provided
  if (typeof fallbackDate === 'string' && fallbackDate.trim().length > 0) {
    return {
      factLabel: 'First seen',
      effectiveDate: fallbackDate.trim(),
      isDerived: true,
      createdBy: null,
      createdByName: null,
    };
  }

  // Derive from earliest activity event
  let earliestDate: string | null = null;
  if (Array.isArray(events)) {
    for (const ev of events) {
      if (ev && typeof ev.created_at === 'string' && ev.created_at.trim().length > 0) {
        const d = ev.created_at.trim();
        if (!earliestDate || d < earliestDate) {
          earliestDate = d;
        }
      }
    }
  }

  if (earliestDate) {
    return {
      factLabel: 'First seen',
      effectiveDate: earliestDate,
      isDerived: true,
      createdBy: null,
      createdByName: null,
    };
  }

  return {
    factLabel: 'Record created',
    effectiveDate: null,
    isDerived: false,
    createdBy: null,
    createdByName: null,
  };
}
